import { useEffect, useState, type ClipboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { reportsApi, type ReportKind } from '../api';
import { useAction } from '../hooks/useAction';
import { t } from '../i18n';
import { recentErrors } from '../lib/clientErrors';
import { toast } from '../stores/toast';
import { cx } from '../lib/cx';
import { Button, IconButton } from './ui';
import { IconBug, IconClose } from './ui/Icon';

const MAX_SHOTS = 3;
const MAX_SIDE = 1920;
const MAX_BYTES = 4.5 * 1024 * 1024;   // API приймає до 5 МБ на знімок, base64 — ще +33 %

/** Зображення → JPEG data URL не більше MAX_SIDE по довшій стороні (знімок 4K-екрана інакше важить десятки МБ). */
function toDataUrl(src: CanvasImageSource, w: number, h: number): string {
  const k = Math.min(1, MAX_SIDE / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.round(w * k);
  c.height = Math.round(h * k);
  const g = c.getContext('2d')!;
  // JPEG без альфа-каналу: прозорі місця PNG інакше стають чорними
  g.fillStyle = '#fff';
  g.fillRect(0, 0, c.width, c.height);
  g.drawImage(src, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.85);
}

async function fileToDataUrl(f: Blob): Promise<string> {
  const bmp = await createImageBitmap(f);
  try {
    return toDataUrl(bmp, bmp.width, bmp.height);
  } finally {
    bmp.close();
  }
}

/** Знімок екрана через вибір вкладки/вікна в браузері (getDisplayMedia): один кадр, потік одразу зупиняється. */
async function captureScreen(): Promise<string> {
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  try {
    const v = document.createElement('video');
    v.srcObject = stream;
    v.muted = true;
    await v.play();
    await new Promise((r) => setTimeout(r, 250));   // перший кадр після перемальовки без діалогу
    return toDataUrl(v, v.videoWidth, v.videoHeight);
  } finally {
    stream.getTracks().forEach((tr) => tr.stop());
  }
}

const OPEN_EVENT = 'xq:report-open';
/** Те саме імʼя стирає stores/auth.ts при виході. */
const DRAFT_KEY = 'xq.report.draft';

/** Відкрити віконце звіту з будь-якого місця (напр. кнопкою на сторінці «Звіти про проблеми»). */
export function openReportWidget(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function loadDraft(): string {
  try {
    return localStorage.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveDraft(v: string): void {
  try {
    if (v) localStorage.setItem(DRAFT_KEY, v);
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* приватний режим — чернетка просто не переживе перезавантаження */
  }
}

/**
 * Віконце «Є проблема?» внизу кожного екрана CRM (AppLayout): написати, що сталося, додати знімок — і в базу звітів.
 * Не модальне: сторінкою можна користуватися далі, а недописаний текст переживає перехід між екранами.
 */
export function ReportWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ReportKind>('bug');
  const [body, setBody] = useState(loadDraft);
  const [shots, setShots] = useState<string[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [withErrors, setWithErrors] = useState(true);
  // після відправки віконце не зникає мовчки: показує номер звіту і куди дивитись (тост у кутку легко пропустити)
  const [sent, setSent] = useState<string | null>(null);
  const errors = recentErrors();

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);
  useEffect(() => saveDraft(body), [body]);

  const add = (url: string) => {
    if (url.length * 0.75 > MAX_BYTES) return toast.error(t('rep.tooLarge'));
    setShots((s) => (s.length >= MAX_SHOTS ? s : [...s, url]));
  };
  const addFiles = async (files: Iterable<Blob>) => {
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      try {
        add(await fileToDataUrl(f));
      } catch {
        toast.error(t('rep.badImage'));
      }
    }
  };
  const capture = async () => {
    setCapturing(true);   // віконце не має потрапити на знімок
    try {
      add(await captureScreen());
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'NotAllowedError')) toast.error(t('rep.captureFailed'));
    } finally {
      setCapturing(false);
    }
  };
  const onPaste = (e: ClipboardEvent) => {
    const imgs = [...e.clipboardData.items].filter((i) => i.type.startsWith('image/')).map((i) => i.getAsFile()!).filter(Boolean);
    if (imgs.length) {
      e.preventDefault();
      void addFiles(imgs);
    }
  };

  const send = useAction(
    (_: void) =>
      reportsApi.create({
        kind,
        body: body.trim(),
        app: 'crm',
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) || import.meta.env.MODE,
        client_errors: withErrors ? errors : [],
        screenshots: shots,
      }),
    {
      invalidate: [['reports']],
      onSuccess: (r) => {
        setBody('');
        setShots([]);
        setKind('bug');
        setSent(r.number ?? '');
      },
    },
  );
  const canCapture = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  const kinds: { value: ReportKind; label: string }[] = [
    { value: 'bug', label: t('rep.kind.bug') },
    { value: 'idea', label: t('rep.kind.idea') },
    { value: 'question', label: t('rep.kind.question') },
  ];

  const close = () => {
    setOpen(false);
    setSent(null);
  };
  if (capturing) return null;
  if (open && sent !== null) {
    return (
      <section
        aria-label={t('rep.title')}
        role="status"
        className="fixed z-40 bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] bg-bg border border-line rounded-xl shadow-2xl flex flex-col items-center text-center gap-3 px-6 py-6 print:hidden"
      >
        <span className="w-14 h-14 rounded-full bg-tile flex items-center justify-center text-fg" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </span>
        <h2 className="m-0 text-lg font-bold">{t('rep.doneTitle', { n: sent })}</h2>
        <p className="m-0 text-sm text-lab">{t('rep.doneText')}</p>
        <div className="flex flex-wrap justify-center gap-2 mt-1">
          <Link className="btn btn-solid" to="/reports" onClick={close}>
            {t('rep.doneView')}
          </Link>
          <Button onClick={() => setSent(null)}>{t('rep.doneMore')}</Button>
        </div>
        <button type="button" className="text-sm text-lab hover:text-fg" onClick={close}>
          {t('common.close')}
        </button>
      </section>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-4 right-4 flex items-center gap-2 h-10 px-4 rounded-full border border-line bg-bg text-sm font-semibold shadow-lift hover:border-line-strong print:hidden"
      >
        <IconBug size={16} />
        {body ? t('rep.draft') : t('rep.fab')}
      </button>
    );
  }
  return (
    <section
      aria-label={t('rep.title')}
      onPaste={onPaste}
      className="fixed z-40 bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] bg-bg border border-line rounded-xl shadow-2xl flex flex-col print:hidden"
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-3">
        <h2 className="m-0 text-base font-bold flex items-center gap-2">
          <IconBug size={16} />
          {t('rep.title')}
        </h2>
        <IconButton label={t('common.close')} onClick={close}>
          <IconClose size={14} />
        </IconButton>
      </div>
      <div className="px-4 py-3 flex flex-col gap-3">
        <div role="radiogroup" aria-label={t('rep.kind')} className="flex flex-wrap gap-1.5">
          {kinds.map((k) => (
            <button
              key={k.value}
              type="button"
              role="radio"
              aria-checked={kind === k.value}
              onClick={() => setKind(k.value)}
              className={cx('chip !h-7 !px-2.5 text-xs', kind === k.value && '!bg-fg !text-bg')}
            >
              {k.label}
            </button>
          ))}
        </div>
        <textarea
          className="textarea"
          aria-label={t('rep.body')}
          placeholder={t('rep.bodyHint')}
          rows={4}
          maxLength={10000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          autoFocus
        />
        {shots.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {shots.map((s, i) => (
              <div key={i} className="relative">
                <img src={s} alt={t('rep.shotAlt', { n: i + 1 })} className="h-14 rounded border border-line object-cover" />
                <IconButton
                  label={t('rep.removeShot')}
                  className="!absolute -top-2 -right-2 !w-6 !h-6 bg-bg border border-line"
                  onClick={() => setShots((x) => x.filter((_, j) => j !== i))}
                >
                  <IconClose size={12} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          {shots.length < MAX_SHOTS && canCapture && (
            <Button size="sm" onClick={() => void capture()}>
              {t('rep.capture')}
            </Button>
          )}
          {shots.length < MAX_SHOTS && (
            <label className="btn btn-sm cursor-pointer">
              {t('rep.upload')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="sr-only"
                onChange={(e) => {
                  void addFiles(e.target.files ?? []);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        <p className="text-xs text-lab m-0">{t('rep.pasteHint')} {t('rep.auto')}</p>
        {errors.length > 0 && (
          <label className="flex gap-2 items-center text-xs">
            <input type="checkbox" checked={withErrors} onChange={(e) => setWithErrors(e.target.checked)} />
            {t('rep.withErrors', { n: errors.length })}
          </label>
        )}
      </div>
      <div className="flex items-center gap-2 px-4 py-3 border-t border-line bg-tile rounded-b-xl">
        {pathname !== '/reports' && (
          <Link className="text-sm text-lab hover:text-fg mr-auto" to="/reports" onClick={close}>
            {t('rep.mine')}
          </Link>
        )}
        <Button size="sm" variant="solid" className="ml-auto" busy={send.isPending} disabled={!body.trim()} onClick={() => send.mutate()}>
          {t('rep.send')}
        </Button>
      </div>
    </section>
  );
}
